/**
 * Конфигурация тем Material UI для АСУБП.
 * Тёмная тема — основная, светлая — альтернативная.
 * Цвета и шрифты определяются текущим брендом (VITE_BRAND).
 */
import { createTheme, type PaletteMode } from '@mui/material/styles';
import { getBrand } from './brands';

export function createAppTheme(mode: PaletteMode) {
  const isDark = mode === 'dark';
  const brand = getBrand();

  return createTheme({
    palette: {
      mode,
      primary: {
        main: brand.primary,
        light: brand.primaryLight,
        dark: brand.primaryDark,
      },
      secondary: {
        main: brand.secondary,
      },
      background: isDark
        ? { default: '#0A0E17', paper: '#111827' }
        : { default: '#F3F4F6', paper: '#FFFFFF' },
      success: { main: brand.success },
      warning: { main: brand.warning },
      error: { main: brand.error },
      info: { main: brand.primary },
      divider: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)',
      text: isDark
        ? { primary: '#F9FAFB', secondary: '#9CA3AF' }
        : { primary: '#111827', secondary: '#6B7280' },
    },
    typography: {
      fontFamily: `${brand.fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
      h1: { fontFamily: `${brand.headingFontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
      h2: { fontFamily: `${brand.headingFontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
      h3: { fontFamily: `${brand.headingFontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
      h4: {
        fontFamily: `${brand.headingFontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
        fontWeight: 700,
        fontSize: '1.75rem',
        '@media (max-width: 600px)': {
          fontSize: '1.25rem',
        },
      },
      h5: {
        fontFamily: `${brand.headingFontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
        fontWeight: 600,
        fontSize: '1.5rem',
        '@media (max-width: 600px)': {
          fontSize: '1.125rem',
        },
      },
      h6: {
        fontFamily: `${brand.headingFontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
        fontWeight: 600,
        fontSize: '1.25rem',
        '@media (max-width: 600px)': {
          fontSize: '1rem',
        },
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            fontWeight: 600,
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: isDark
              ? '0 2px 12px rgba(0,0,0,0.3)'
              : '0 2px 12px rgba(0,0,0,0.08)',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined' as const,
          size: 'small' as const,
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isDark
              ? '0 1px 3px rgba(0,0,0,0.3)'
              : '0 1px 3px rgba(0,0,0,0.12)',
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: 'none',
            boxShadow: isDark
              ? '2px 0 8px rgba(0,0,0,0.3)'
              : '2px 0 8px rgba(0,0,0,0.05)',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB',
              fontWeight: 600,
              fontSize: 13,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(0,0,0,0.02)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          size: 'small' as const,
        },
        styleOverrides: {
          // Paper (контейнер внутри Popper) — расширяется по контенту
          paper: {
            width: 'max-content',
            maxWidth: '80vw',
          },
          listbox: {
            maxHeight: '50vh',
          },
          option: {
            whiteSpace: 'nowrap' as const,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
          },
        },
      },
    },
  });
}
